<template>
	<div class="smart-home-container">
		<!-- 左侧手机展示 -->
		<div class="phone-display"></div>

		<!-- 右侧内容 -->
		<div class="content">
			<h1>{{ $t('smartHome.title') }}</h1>
			<p>{{ $t('smartHome.description') }}</p>
			<div class="download-buttons">
				<a :href="iosDownloadUrl || 'javascript:void(0)'" class="download-btn ios-btn" @click.prevent="handleDownload('ios')">
					<i class="fab fa-apple"></i>
					{{ $t('smartHome.downloadIOS') }}
				</a>
				<a :href="androidDownloadUrl || 'javascript:void(0)'" class="download-btn android-btn" @click.prevent="handleDownload('android')">
					<i class="fab fa-android"></i>
					{{ $t('smartHome.downloadAndroid') }}
				</a>
			</div>
		</div>
	</div>
</template>

<script>
export default {
	data() {
		return {
			browserLanguage: '',
			current: 1, // 0:海外, 1:国内
			iosDownloadUrl: '',
			androidDownloadUrl: '',
			urlObj: null,
			config: [
				{
					dataCenterType: 0,
					publicAppId: 'usx07yqia4diqi', // 海外
					apiPath: 'https://service.hellobabysmart.com/api',
					logo: '../static/logo_en.png',
					android: '../static/android_en.png',
					ios: '../static/ios_en.png'
				},
				{
					dataCenterType: 1,
					publicAppId: 'cnla3t0a8tqm7t', //国内
					apiPath: 'https://service.rinoiot.com/api',
					logo: '../static/logo_cn.png',
					android: '../static/android_cn.png',
					ios: '../static/ios_cn.png'
				}
			],
			cacheKey: 'download_config_cache',
			cacheExpire: 24 * 60 * 60 * 1000, // 24小时缓存有效期
			isLoading: false
		};
	},
	created() {
		this.checkCache();
	},
	mounted() {
		this.browserLanguage = (navigator.browserLanguage || navigator.language).toLowerCase();

		if (!this.urlObj) {
			this.initDownloadConfig();
		}
	},
	methods: {
		// 检查本地缓存
		checkCache() {
			try {
				const cacheStr = localStorage.getItem(this.cacheKey);
				if (!cacheStr) return false;

				const cacheData = JSON.parse(cacheStr);
				if (Date.now() - cacheData.timestamp < this.cacheExpire) {
					this.urlObj = cacheData.data;
					this.current = cacheData.region;
					this.preFetchDownloadUrls();
					return true;
				}
				localStorage.removeItem(this.cacheKey);
				return false;
			} catch (e) {
				return false;
			}
		},

		saveToCache() {
			try {
				const cacheData = {
					data: this.urlObj,
					region: this.current,
					timestamp: Date.now()
				};
				localStorage.setItem(this.cacheKey, JSON.stringify(cacheData));
			} catch (e) {}
		},

		async initDownloadConfig() {
			if (this.isLoading) return;

			this.isLoading = true;
			try {
				await this.getIP();

				await this.fetchDownloadConfig(this.config[this.current].publicAppId);

				this.saveToCache();
			} catch (error) {
				this.preFetchDownloadUrls();
			} finally {
				this.isLoading = false;
			}
		},

		requestUrl(id) {
			return `${this.config[this.current].apiPath}/business-app/v1/h5/getDownloadConfig/${id || ''}`;
		},

		async fetchDownloadConfig(id) {
			try {
				const response = await fetch(this.requestUrl(id), {
					method: 'POST',
					headers: {
						'Content-Type': 'application/json'
					}
				});

				if (!response.ok) {
					throw new Error('Network response was not ok');
				}

				const res = await response.json();
				this.urlObj = res.data || {};
				this.preFetchDownloadUrls();
			} catch (err) {
				throw err;
			}
		},

		async preFetchDownloadUrls() {
			if (!this.urlObj) return;
			const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

			let iosUrl = '';
			let androidUrl = '';

			iosUrl = this.urlObj.appStoreUrl;

			if (!isMobile) {
				if (this.current === 0 && this.urlObj.googleStoreUrl) {
					androidUrl = this.urlObj.googleStoreUrl;
				} else {
					androidUrl = this.urlObj.tencentStoreUrl;
				}
			} else {
				const brandChecks = [
					{ check: this.isHuawei, urlKey: 'huaweiStoreUrl' },
					{ check: this.isXiaomi, urlKey: 'xiaomiStoreUrl' },
					{ check: this.isVivo, urlKey: 'vivoStoreUrl' },
					{ check: this.isOppo, urlKey: 'oppoStoreUrl' }
				];

				let foundBrandUrl = null;
				for (const { check, urlKey } of brandChecks) {
					if (check.call(this) && this.urlObj[urlKey]) {
						foundBrandUrl = this.urlObj[urlKey];
						break;
					}
				}

				androidUrl = foundBrandUrl || this.urlObj.tencentStoreUrl;

				if (this.current === 0 && !foundBrandUrl && this.urlObj.googleStoreUrl) {
					androidUrl = this.urlObj.googleStoreUrl;
				}
			}

			this.$nextTick(() => {
				this.iosDownloadUrl = iosUrl || 'javascript:void(0)';
				this.androidDownloadUrl = androidUrl || 'javascript:void(0)';
			});
		},

		getIP() {
			return new Promise((resolve, reject) => {
				if (typeof this.current !== 'undefined' && [0, 1].includes(this.current)) {
					resolve();
					return;
				}

				fetch('https://ipinfo.io/json')
					.then((response) => {
						if (!response.ok) {
							throw new Error('Network response was not ok');
						}
						return response.json();
					})
					.then((data) => {
						this.current = data && data.country === 'CN' ? 1 : 0;
						resolve();
					})
					.catch((error) => {
						this.current = this.browserLanguage.includes('zh') ? 1 : 0;
						resolve();
					});
			});
		},

		// 设备检测方法保持不变
		isIos() {
			const userAgent = window.navigator.userAgent || '';
			const isIosDevice = /iPhone|iPad|iPod|Macintosh/i.test(userAgent) && 'ontouchend' in document;
			return !!isIosDevice;
		},

		isAndroid() {
			const userAgent = window.navigator.userAgent || '';
			return userAgent.indexOf('Android') > -1 || userAgent.indexOf('Linux') > -1;
		},

		isHuawei() {
			const userAgentStr = navigator.userAgent.toLowerCase();
			return userAgentStr.includes('huawei') || userAgentStr.includes('honor');
		},

		isXiaomi() {
			const userAgentStr = navigator.userAgent.toLowerCase();
			return (
				userAgentStr.includes('mi ') ||
				userAgentStr.includes('mix ') ||
				userAgentStr.includes('redmi') ||
				userAgentStr.includes('xiaomi') ||
				userAgentStr.includes('m2012k11ac')
			);
		},

		isVivo() {
			const userAgentStr = navigator.userAgent.toLowerCase();
			return userAgentStr.includes('vivo');
		},

		isOppo() {
			const userAgentStr = navigator.userAgent.toLowerCase();
			return userAgentStr.includes('oppo') || userAgentStr.includes('pacm00') || userAgentStr.includes('pbem00');
		},

		handleDownload(platform) {
			const url = platform === 'ios' ? this.iosDownloadUrl : this.androidDownloadUrl;

			if (!url || url === 'javascript:void(0)') {
				console.error('下载地址不可用');
				return;
			}

			window.location.href = url;
		}
	}
};
</script>
<style scoped>
.smart-home-container {
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 40px 18px;
	aspect-ratio: 1920/732;
	background-image: url('../assets/home/download.png');
	background-size: cover;
	background-repeat: no-repeat;
	background-position: center left;
}

.phone-display {
	flex: 1;
	max-width: calc(995 / 1840 * 100%);
}

.content {
	flex: 1;
	max-width: 500px;
}

h1 {
	font-size: 2.5rem;
	color: #333;
	margin-bottom: 30px;
}

p {
	font-size: 19px;
	color: #666;
	line-height: 1.6;
	margin-bottom: 35px;
}

.download-buttons {
	display: flex;
	gap: 15px;
}

.download-btn {
	display: flex;
	align-items: center;
	justify-content: center;
	width: 164px;
	padding: 12px 24px;
	border-radius: 30px;
	text-decoration: none;
	font-weight: 500;
	transition: all 0.3s ease;
}

.ios-btn {
	background-color: #30b4e9;
	color: white;
}

.android-btn {
	border: 1px solid #30b4e9;
	color: #30b4e9;
}

.download-btn:hover {
	opacity: 0.9;
	transform: translateY(-2px);
}

.download-btn i {
	margin-right: 8px;
	font-size: 1.2rem;
}
</style>
